import type { CanvasState } from '../hooks/useCanvasTransform';
import type { CanvasObject, Artboard } from '../types';

export interface HitTestImageParams {
  objects: CanvasObject[];
  objectsMap: Map<string, CanvasObject>;
  artboardsMap: Map<string, Artboard>;
  canvasState: CanvasState;
  canvas: HTMLCanvasElement | null;
}

/**
 * 마우스 위치에서 가장 위에 있는 이미지를 찾는 히트테스트 유틸
 * - viewport culling
 * - 아트보드 경계
 * - 클리핑 마스크
 * 를 모두 고려합니다.
 */
export function hitTestImageAtPoint(
  x: number,
  y: number,
  {
    objects,
    objectsMap,
    artboardsMap,
    canvasState,
    canvas
  }: HitTestImageParams
): { id: string; hash: string | null } | null {
  if (!canvas) return null;

  const { scale, offset } = canvasState;

  // viewport 영역 계산
  const viewLeft = -offset.x / scale;
  const viewTop = -offset.y / scale;
  const viewRight = (canvas.width - offset.x) / scale;
  const viewBottom = (canvas.height - offset.y) / scale;

  // zOrder 상단 우선: 배열의 끝에서부터 순회
  for (let i = objects.length - 1; i >= 0; i--) {
    const obj = objects[i];
    if (obj.type !== 'image' || !obj.image) continue;
    if (obj.isLocked) continue;

    // viewport culling
    const objRight = obj.x + obj.width;
    const objBottom = obj.y + obj.height;
    if (
      obj.x > viewRight ||
      objRight < viewLeft ||
      obj.y > viewBottom ||
      objBottom < viewTop
    ) {
      continue;
    }

    // 회전 고려 히트테스트
    const angle = obj.rotation || 0;
    let tx = x;
    let ty = y;
    if (Math.abs(angle) > 1e-6) {
      const cx = obj.x + obj.width / 2;
      const cy = obj.y + obj.height / 2;
      const dx = x - cx;
      const dy = y - cy;
      const cos = Math.cos(-angle);
      const sin = Math.sin(-angle);
      tx = cx + dx * cos - dy * sin;
      ty = cy + dx * sin + dy * cos;
    }

    // 바운딩 박스 체크
    if (!(tx >= obj.x && tx <= obj.x + obj.width && ty >= obj.y && ty <= obj.y + obj.height)) {
      continue;
    }

    // 아트보드 경계 체크
    if (obj.artboardId) {
      const artboard = artboardsMap.get(obj.artboardId);
      if (artboard) {
        if (
          !(
            x >= artboard.x &&
            x <= artboard.x + artboard.width &&
            y >= artboard.y &&
            y <= artboard.y + artboard.height
          )
        ) {
          continue;
        }
      }
    }

    // 클리핑 마스크 체크
    if (obj.isClipped && obj.clipMaskId) {
      const maskObj = objectsMap.get(obj.clipMaskId);
      if (maskObj) {
        const maskAngle = maskObj.rotation || 0;
        let maskTx = x;
        let maskTy = y;
        if (Math.abs(maskAngle) > 1e-6) {
          const mcx = maskObj.x + maskObj.width / 2;
          const mcy = maskObj.y + maskObj.height / 2;
          const mdx = x - mcx;
          const mdy = y - mcy;
          const mcos = Math.cos(-maskAngle);
          const msin = Math.sin(-maskAngle);
          maskTx = mcx + mdx * mcos - mdy * msin;
          maskTy = mcy + mdx * msin + mdy * mcos;
        }

        let isInMask = false;
        if (maskObj.type === 'rectangle') {
          isInMask =
            maskTx >= maskObj.x &&
            maskTx <= maskObj.x + maskObj.width &&
            maskTy >= maskObj.y &&
            maskTy <= maskObj.y + maskObj.height;
        } else if (maskObj.type === 'circle') {
          const mcx = maskObj.x + maskObj.width / 2;
          const mcy = maskObj.y + maskObj.height / 2;
          const mrx = maskObj.width / 2;
          const mry = maskObj.height / 2;
          if (mrx > 0 && mry > 0) {
            const mnx = (maskTx - mcx) / mrx;
            const mny = (maskTy - mcy) / mry;
            isInMask = mnx * mnx + mny * mny <= 1;
          }
        } else {
          isInMask =
            maskTx >= maskObj.x &&
            maskTx <= maskObj.x + maskObj.width &&
            maskTy >= maskObj.y &&
            maskTy <= maskObj.y + maskObj.height;
        }

        if (!isInMask) {
          continue;
        }
      }
    }

    return {
      id: obj.id,
      hash: obj.imageHash || null
    };
  }

  return null;
}


